import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Check, Eraser, PenLine, ThumbsUp, MessageSquareWarning, Undo2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const base = (token) => `${BACKEND}/api/public/concept-design/${token}`;

function SignaturePad({ onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} data-testid="concept-sign-pad"
        className="w-full h-32 rounded-lg border-2 border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

function DrawCanvas({ imgUrl, onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => {
      const maxW = 760; const scale = Math.min(1, maxW / img.width);
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height); setReady(true);
    };
    img.onerror = () => setReady(true);
    img.src = imgUrl;
  }, [imgUrl]);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#dc2626"; ctx.lineWidth = 3.5; ctx.lineCap = "round"; ctx.stroke(); e.preventDefault(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; try { onChange(ref.current.toDataURL("image/png")); } catch { /* tainted */ } };
  return (
    <div>
      <p className="text-[12px] text-slate-500 mb-1 flex items-center gap-1"><PenLine className="h-3.5 w-3.5 text-red-500" />在方案图上手绘修改（红色）· Draw on the plan</p>
      <canvas ref={ref} data-testid="concept-draw-canvas"
        className="w-full rounded-lg border border-slate-200 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      {!ready && <p className="text-[11px] text-slate-400 mt-1">加载方案图… Loading…</p>}
    </div>
  );
}

function HighlightCompare({ prevUrl, latestUrl, testid }) {
  const cRef = useRef();
  const [on, setOn] = useState(false);
  const [ready, setReady] = useState(false);
  const [changePct, setChangePct] = useState(null);
  useEffect(() => {
    if (!on) return;
    setReady(false); setChangePct(null);
    const load = (src) => new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = rej; im.src = src; });
    Promise.all([load(prevUrl), load(latestUrl)]).then(([prev, latest]) => {
      const maxW = 720; const scale = Math.min(1, maxW / latest.width);
      const w = Math.max(1, Math.round(latest.width * scale)), h = Math.max(1, Math.round(latest.height * scale));
      const c = cRef.current; if (!c) return; c.width = w; c.height = h;
      const ctx = c.getContext("2d"); ctx.drawImage(latest, 0, 0, w, h);
      const off = document.createElement("canvas"); off.width = w; off.height = h;
      off.getContext("2d").drawImage(prev, 0, 0, w, h);
      try {
        const a = ctx.getImageData(0, 0, w, h); const bdat = off.getContext("2d").getImageData(0, 0, w, h);
        const d = a.data, pd = bdat.data; let changed = 0; const total = w * h;
        for (let i = 0; i < d.length; i += 4) {
          const diff = Math.abs(d[i] - pd[i]) + Math.abs(d[i + 1] - pd[i + 1]) + Math.abs(d[i + 2] - pd[i + 2]);
          if (diff > 60) { const lum = (d[i] + d[i + 1] + d[i + 2]) / 3; d[i] = Math.min(255, 205 + (lum * 0.2) | 0); d[i + 1] = (lum * 0.18) | 0; d[i + 2] = (lum * 0.18) | 0; changed++; }
        }
        ctx.putImageData(a, 0, 0);
        setChangePct(Math.round((changed / total) * 1000) / 10);
      } catch { setChangePct(null); }
      setReady(true);
    }).catch(() => { setReady(true); });
  }, [on, prevUrl, latestUrl]);
  return (
    <div className="mt-2" data-testid={testid}>
      <button onClick={() => setOn((v) => !v)} data-testid={`${testid}-toggle`}
        className={`text-[12px] inline-flex items-center gap-1 rounded-lg px-2.5 py-1 border font-medium ${on ? "bg-brand-500 text-white border-brand-500" : "bg-white text-brand-500 border-brand-300 hover:bg-brand-50"}`}>
        <span className="h-2 w-2 rounded-full bg-current" />{on ? "隐藏改动高亮 Hide highlight" : "高亮本次改动 Highlight changes"}
      </button>
      {on && (
        <div className="mt-2">
          {!ready && <p className="text-[11px] text-slate-400">正在比对… Comparing…</p>}
          <canvas ref={cRef} data-testid={`${testid}-canvas`} className="w-full rounded-lg border-2 border-red-200 bg-slate-50" />
          {ready && (changePct != null
            ? <p className="text-[11px] text-red-500 mt-1" data-testid={`${testid}-pct`}>红色部分为相较上一版的改动区域，约 {changePct}% 面积有变化。Red = changed vs previous (~{changePct}%).</p>
            : <p className="text-[11px] text-slate-400 mt-1">两版尺寸差异较大，仅供并排参考。</p>)}
        </div>
      )}
    </div>
  );
}

export default function ConceptDesignPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null); // 'agree' | 'revise'
  const [signer, setSigner] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [sig, setSig] = useState(null);
  const [comment, setComment] = useState("");
  const [drawing, setDrawing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [prefer, setPrefer] = useState(null);
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState("");
  const [noteSent, setNoteSent] = useState(false);

  const pick = async (pid) => {
    setPicking(true);
    try {
      const r = await fetch(`${base(token)}/prefer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ planId: pid }) });
      if (!r.ok) throw new Error();
      setPrefer(pid); setNoteSent(false);
    } catch { alert("提交失败，请重试 · Failed"); }
    setPicking(false);
  };

  const sendNote = async (pid) => {
    if (!pid || !note.trim()) return;
    setPicking(true);
    try {
      const r = await fetch(`${base(token)}/prefer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ planId: pid, note: note.trim() }) });
      if (!r.ok) throw new Error();
      setNoteSent(true);
    } catch { alert("提交失败，请重试 · Failed"); }
    setPicking(false);
  };

  const load = useCallback(() => {
    fetch(base(token)).then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setData).catch(() => setErr("链接无效或已失效 · Link invalid or expired"));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!signer.trim()) return alert("请填写您的姓名 · Enter your name");
    if (mode === "agree" && !confirm) return alert("请勾选确认 · Please confirm");
    if (mode === "revise" && !comment.trim() && !drawing) return alert("请填写修改意见或手绘修改 · Add a comment or drawing");
    setSubmitting(true);
    try {
      const res = await fetch(`${base(token)}/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision: mode, signerName: signer, comment, signature: mode === "agree" ? sig : null, drawing: mode === "revise" ? drawing : null }),
      });
      if (!res.ok) throw new Error();
      setDone(true); load();
    } catch { alert("提交失败，请重试 · Failed"); } finally { setSubmitting(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center" data-testid="concept-portal-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />加载中 Loading…</div>;

  const planFiles = (data.files && data.files.length) ? data.files : (data.finalFile ? [data.finalFile] : []);
  const prevFiles = (data.previousFiles && data.previousFiles.length) ? data.previousFiles : (data.previousFile ? [data.previousFile] : []);
  const firstImg = planFiles.find((f) => f.previewable);
  const drawTarget = firstImg ? `${base(token)}/file/${firstImg.fileId}` : (data.layoutPlans[0] ? `${base(token)}/file/${data.layoutPlans[0].fileId}` : null);
  const already = data.clientResponse;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="concept-portal-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-4">
          <p className="text-xs font-bold text-emerald-600">ALIGCUS · ADU 概念方案确认 Concept Design</p>
          <h1 className="text-xl font-bold text-slate-800" data-keep-title>{data.address}</h1>
          <p className="text-sm text-slate-500">{data.clientName}{data.city ? ` · ${data.city}` : ""}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-5 space-y-4">
        {/* Plan viewer (支持多图方案 + 版本对比 + 改动高亮) */}
        <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="concept-portal-viewer">
          <p className="text-sm font-semibold text-slate-700 mb-2">概念方案设计文件 Concept Design{data.version > 1 ? ` · 第 ${data.version} 版 v${data.version}` : ""}{planFiles.length > 1 ? ` · ${planFiles.length} 张` : ""}</p>
          {planFiles.length > 0 ? (
            <div className="space-y-4">
              {planFiles.map((f, idx) => {
                const url = `${base(token)}/file/${f.fileId}`;
                const prev = prevFiles[idx];
                const prevUrl = prev && prev.previewable ? `${base(token)}/file/${prev.fileId}` : null;
                return (
                  <div key={f.fileId} data-testid={`concept-plan-${idx}`}>
                    {(f.label || planFiles.length > 1) && <p className="text-[12px] font-semibold text-slate-600 mb-1">{f.label || f.name}</p>}
                    {prevUrl && f.previewable ? (
                      <div data-testid={`concept-version-compare-${idx}`}>
                        <p className="text-[12px] font-semibold text-amber-600 mb-1">版本对比 Version compare（v{prev.version} → v{data.version}）</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[11px] text-slate-400 mb-1">上一版 v{prev.version}</p>
                            <a href={prevUrl} target="_blank" rel="noreferrer"><img alt="prev" src={prevUrl} className="w-full h-56 object-contain bg-slate-50 rounded-lg border border-slate-200" /></a>
                          </div>
                          <div>
                            <p className="text-[11px] text-emerald-600 font-semibold mb-1">最新版 v{data.version}</p>
                            <a href={url} target="_blank" rel="noreferrer"><img alt="latest" src={url} className="w-full h-56 object-contain bg-slate-50 rounded-lg border-2 border-emerald-300" /></a>
                          </div>
                        </div>
                        <HighlightCompare prevUrl={prevUrl} latestUrl={url} testid={`concept-highlight-${idx}`} />
                      </div>
                    ) : f.previewable ? (
                      <a href={url} target="_blank" rel="noreferrer"><img alt={f.name} src={url} className="w-full rounded-lg border border-slate-100" /></a>
                    ) : f.ext === "pdf" ? (
                      <iframe title={f.name} src={url} className="w-full h-[60vh] rounded-lg border border-slate-100" />
                    ) : (
                      <a href={url} target="_blank" rel="noreferrer" className="inline-block text-sm text-indigo-600 underline">下载查看 {f.name}</a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-slate-400 py-6 text-center">暂无方案文件</p>}
          {data.layoutPlans.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
              <p className="w-full text-[11px] text-slate-400">卫星布局参考 Layout references</p>
              {data.layoutPlans.map((p) => <a key={p.aid} href={`${base(token)}/file/${p.fileId}`} target="_blank" rel="noreferrer" className="block"><img alt={p.name} src={`${base(token)}/file/${p.fileId}`} className="h-20 w-20 object-cover rounded-lg border border-slate-200" /></a>)}
            </div>
          )}
        </div>

        {/* 多方案对比 · 客户挑选偏好 */}
        {data.layoutPlans.length >= 2 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="concept-compare-section">
            <p className="text-sm font-semibold text-slate-700">布局方案对比 · 请选择您更喜欢的方案 Compare & pick a favourite</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {data.layoutPlans.map((p) => {
                const chosen = (prefer || data.clientPreferredPlanId) === p.aid;
                return (
                  <div key={p.aid} data-testid={`concept-compare-${p.aid}`} className={`rounded-xl border-2 p-2 ${chosen ? "border-emerald-500 bg-emerald-50/60" : "border-slate-200"}`}>
                    <a href={`${base(token)}/file/${p.fileId}`} target="_blank" rel="noreferrer">
                      <img alt={p.name} src={`${base(token)}/file/${p.fileId}`} className="w-full h-44 object-contain bg-slate-50 rounded-lg" />
                    </a>
                    <div className="flex items-center justify-between mt-2 gap-2">
                      <p className="text-[12px] font-semibold text-slate-600 truncate">{p.name}</p>
                      <button onClick={() => pick(p.aid)} disabled={picking} data-testid={`concept-prefer-${p.aid}`}
                        className={`shrink-0 text-[12px] rounded-lg px-2.5 py-1 border font-semibold disabled:opacity-50 ${chosen ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}>
                        {chosen ? "已选择 ✓ Picked" : "更喜欢这个 Prefer"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {(prefer || data.clientPreferredPlanId) && (
              <>
                <p className="text-[11px] text-emerald-600 mt-2" data-testid="concept-prefer-done">已记录您的偏好，顾问会据此推进。Your preference has been shared with your consultant.</p>
                <div className="mt-2 flex items-start gap-2" data-testid="concept-prefer-note-box">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2}
                    placeholder="告诉我们您喜欢它的原因（可选）· What do you like about it? (optional)"
                    className="flex-1 text-[12px] border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                    data-testid="concept-prefer-note-input" />
                  <button onClick={() => sendNote(prefer || data.clientPreferredPlanId)} disabled={picking || !note.trim() || noteSent}
                    data-testid="concept-prefer-note-send"
                    className="shrink-0 text-[12px] rounded-lg px-2.5 py-1.5 bg-emerald-600 text-white font-semibold disabled:opacity-50">
                    {noteSent ? "已发送 ✓ Sent" : "发送留言 Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {(done || already) ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="concept-portal-receipt">
            <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-base font-bold text-emerald-700">{(already?.decision || mode) === "agree" ? "已确认签署 · Approved & Signed" : "修改意见已提交 · Revision submitted"}</p>
            <p className="text-[12px] text-slate-500 mt-1">感谢您的反馈，顾问会尽快与您联系。Thank you — your consultant will follow up.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="concept-portal-actions">
            <p className="text-sm font-semibold text-slate-700 mb-3">请确认方案 · Your decision</p>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setMode("agree")} data-testid="concept-choose-agree" className={`flex-1 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 border ${mode === "agree" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}><ThumbsUp className="h-4 w-4" />签字同意 Approve</button>
              <button onClick={() => setMode("revise")} data-testid="concept-choose-revise" className={`flex-1 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 border ${mode === "revise" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200"}`}><MessageSquareWarning className="h-4 w-4" />提出修改 Request changes</button>
            </div>

            {mode && (
              <div className="space-y-3">
                <input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="您的姓名 Your full name" data-testid="concept-signer" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2" />

                {mode === "agree" && (
                  <>
                    <p className="text-[12px] text-slate-500">请在下框签名 · Please sign below</p>
                    <SignaturePad onChange={setSig} />
                    <label className="flex items-start gap-2 text-[13px] text-slate-600">
                      <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="concept-confirm" className="mt-0.5 accent-emerald-600" />
                      本人已审阅并同意该概念方案设计 · I have reviewed and approve this concept design.
                    </label>
                  </>
                )}

                {mode === "revise" && (
                  <>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="修改意见（文字）· Your comments" data-testid="concept-comment" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2" />
                    {drawTarget ? <DrawCanvas imgUrl={drawTarget} onChange={setDrawing} /> : <p className="text-[12px] text-slate-400">该方案为文档格式，请用文字说明修改。</p>}
                    {drawing && <button onClick={() => { setDrawing(null); }} className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Undo2 className="h-3 w-3" />清除手绘 Reset drawing（重新加载图后再画）</button>}
                  </>
                )}

                <button onClick={submit} disabled={submitting} data-testid="concept-submit" className={`w-full py-2.5 rounded-lg text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${mode === "agree" ? "bg-emerald-600" : "bg-amber-500"}`}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{mode === "agree" ? "确认并签署 Approve & Sign" : "提交修改意见 Submit"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
